import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Save,
  Description,
  CheckCircle,
  History,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { extractReportsHistory } from "@/fluxvet/utils/questionnaireResponseHelper";
import { HistoryModal } from "./HistoryModal";

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

interface Report {
  id: string;
  content: string;
}

interface ReportsSectionProps {
  control: any;
  watch: any;
  setValue: any;
}

export const ReportsSection: React.FC<ReportsSectionProps> = ({
  control,
  watch,
  setValue,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair relatórios
  const reportsHistory = useMemo(
    () => extractReportsHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const reports: Report[] = watch("reports") || [];
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Report, "id">>({
    content: "",
  });

  // Estado para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState({
    isOpen: false,
  });

  const handleAdd = () => {
    if (!formData.content.trim()) return;
    const newReport: Report = { id: generateId(), ...formData };
    setValue("reports", [...reports, newReport]);
    resetForm();
  };

  const handleEdit = (report: Report) => {
    // Não permite editar item já assinado
    if ((report as any).signed) {
      alert("Este item já foi assinado digitalmente e não pode ser editado.");
      return;
    }

    setEditingId(report.id);
    setFormData({ content: report.content });
  };

  const handleSaveEdit = () => {
    if (!formData.content.trim()) return;
    const updated = reports.map((report) =>
      report.id === editingId ? { ...report, ...formData } : report
    );
    setValue("reports", updated);
    resetForm();
  };

  const handleDelete = (id: string) => {
    // Verifica se o item está assinado
    const report = reports.find((r) => r.id === id);
    if (report && (report as any).signed) {
      alert("Este item já foi assinado digitalmente e não pode ser removido.");
      return;
    }

    setValue(
      "reports",
      reports.filter((report) => report.id !== id)
    );
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ content: "" });
  };

  return (
    <>
    <Box>
      {/* Cabeçalho */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
        <Description sx={{ color: "#00A884" }} />
        <Typography variant="h6" fontWeight={600}>
          Relatórios
        </Typography>
        {reports.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            ({reports.length}{" "}
            {reports.length === 1 ? "relatório" : "relatórios"})
          </Typography>
        )}
          </Stack>
          {reportsHistory.length > 0 && (
            <Button
              size="small"
              startIcon={<History />}
              onClick={() => setHistoryModal({ isOpen: true })}
              sx={{
                textTransform: "none",
                fontSize: "0.85rem",
                color: "#666",
                "&:hover": {
                  color: "#00A884",
                  bgcolor: "rgba(0, 168, 132, 0.08)",
                },
              }}
            >
              Ver histórico de relatórios
            </Button>
          )}
      </Stack>

      <Grid container spacing={2}>
        {/* Coluna Esquerda: Formulário (40%) */}
        <Grid item xs={12} md={4.8}>
          <Paper
            key={`report-form-${reports.length}`}
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              bgcolor: "#fafafa",
              height: "100%",
              minHeight: 420,
            }}
          >
            <Stack spacing={2} height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="#00A884">
                {editingId ? "✏️ Editando" : "➕ Novo relatório"}
              </Typography>

              <Divider />

              <TextField
                fullWidth
                label="Conteúdo do relatório *"
                placeholder="Escreva o relatório completo do atendimento..."
                value={editingId ? formData.content : undefined}
                defaultValue=""
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                multiline
                rows={10}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fff",
                  },
                }}
              />

              <Box sx={{ flexGrow: 1 }} />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {editingId && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    sx={{ textTransform: "none" }}
                  >
                    Cancelar
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={editingId ? <Save /> : <Add />}
                  onClick={editingId ? handleSaveEdit : handleAdd}
                  sx={{
                    bgcolor: "#00A884",
                    "&:hover": { bgcolor: "#008c6e" },
                    textTransform: "none",
                  }}
                >
                  {editingId ? "Salvar" : "Adicionar"}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        {/* Coluna Direita: Lista de relatórios (60%) */}
        <Grid item xs={12} md={7.2}>
          {reports.length > 0 ? (
            <Stack spacing={2}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={600}
              >
                RELATÓRIOS ADICIONADOS
              </Typography>
              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "#00A884",
                    borderRadius: "10px",
                  },
                }}
              >
                <Stack spacing={2}>
                  {reports.map((report) => {
                    const isSigned = (report as any).signed;

                    return (
                      <Card
                        key={report.id}
                        sx={{
                          border: isSigned
                            ? "2px solid #00A884"
                            : "1px solid #e0e0e0",
                          transition: "all 0.2s ease",
                          bgcolor: isSigned
                            ? "rgba(0, 168, 132, 0.02)"
                            : "transparent",
                          "&:hover": {
                            borderColor: "#00A884",
                            boxShadow: "0 4px 12px rgba(0, 168, 132, 0.15)",
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="text.secondary"
                                >
                                  Relatório
                                </Typography>
                                {isSigned && (
                                  <CheckCircle
                                    sx={{ fontSize: 18, color: "#00A884" }}
                                  />
                                )}
                              </Stack>
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(report)}
                                  disabled={isSigned}
                                  sx={{
                                    color: isSigned ? "#cccccc" : "#666",
                                    "&:hover": {
                                      color: isSigned ? "#cccccc" : "#00A884",
                                      bgcolor: isSigned
                                        ? "transparent"
                                        : "rgba(0, 168, 132, 0.08)",
                                    },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(report.id)}
                                  disabled={isSigned}
                                  sx={{
                                    color: isSigned ? "#cccccc" : "#666",
                                    "&:hover": {
                                      color: isSigned ? "#cccccc" : "#f44336",
                                      bgcolor: isSigned
                                        ? "transparent"
                                        : "rgba(244, 67, 54, 0.08)",
                                    },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                            <Divider />
                            <Typography
                              variant="body2"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {report.content}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fafafa",
                border: "1px dashed #e0e0e0",
                borderRadius: 2,
                minHeight: 420,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Description sx={{ fontSize: 64, color: "#bdbdbd", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum relatório adicionado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Preencha o campo ao lado e clique em "Adicionar"
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, fontStyle: "italic" }}
              >
                💡 Dica: Use a tecla Tab para navegar entre os campos
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>

    {/* Modal de histórico — só exibido quando há histórico */}
    {reportsHistory.length > 0 && (
      <HistoryModal
        open={historyModal.isOpen}
        onClose={() => setHistoryModal({ isOpen: false })}
        title="Histórico - Relatórios de Atendimento"
        history={reportsHistory}
      />
    )}
  </>
  );
};

export default ReportsSection;
